'use client';

import { useState, useRef, type FormEvent } from 'react';
import type { ContactFormProps } from '@routeroyal/theme-core';
import { analyticsEvents, setEnhancedConversionData } from '@/components/analytics/GoogleAnalytics';
import { collectAttribution } from '@/lib/attribution';

/**
 * Route Royal Contact Form
 *
 * Simple, professional contact form matching the Route Royal brand.
 */
export function RouteRoyalContactForm({
  recipientEmail,
  onSubmit,
}: ContactFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const formStartedRef = useRef(false);

  const handleFormFocus = () => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      analyticsEvents.formStart('contact_form');
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        const attribution = collectAttribution();
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            recipientEmail,
            metadata: attribution,
          }),
        });
        if (!res.ok) throw new Error('Failed to send');
      }

      setStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });

      setEnhancedConversionData({ email: formData.email, phone: formData.phone });
      analyticsEvents.formSubmit('contact_form', window.location.pathname);
      analyticsEvents.generateLead('contact_form');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <section className="py-20 lg:py-28 bg-[#F9FAFB]">
        <div className="max-w-xl mx-auto px-6 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent</h3>
          <p className="text-gray-600">Thank you for getting in touch. We&apos;ll respond shortly.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 lg:py-28 bg-[#F9FAFB]">
      <div className="max-w-xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Get in Touch</h2>
          <p className="text-gray-600">Fill in the form below and we&apos;ll get back to you.</p>
        </div>

        <form onSubmit={handleSubmit} onFocusCapture={handleFormFocus} className="space-y-5">
          <div>
            <label htmlFor="rr-name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              id="rr-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData((d) => ({ ...d, name: e.target.value }))}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none transition"
            />
          </div>

          <div>
            <label htmlFor="rr-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              id="rr-email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData((d) => ({ ...d, email: e.target.value }))}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none transition"
            />
          </div>

          <div>
            <label htmlFor="rr-phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone (Optional)
            </label>
            <input
              id="rr-phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData((d) => ({ ...d, phone: e.target.value }))}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none transition"
            />
          </div>

          <div>
            <label htmlFor="rr-message" className="block text-sm font-medium text-gray-700 mb-1">
              Message
            </label>
            <textarea
              id="rr-message"
              required
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData((d) => ({ ...d, message: e.target.value }))}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#1E3A8A] focus:border-transparent outline-none transition resize-none"
            />
          </div>

          {status === 'error' && (
            <p className="text-sm text-red-600">Something went wrong. Please try again.</p>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full py-3 px-6 bg-[#1E3A8A] text-white font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60 transition-colors"
          >
            {status === 'submitting' ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </section>
  );
}
